/* eslint-disable */
import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Box, Typography, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Tooltip, Skeleton,
} from '@mui/material';
import searchIcon from 'Images/search_icon.svg';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
// import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import iconEye from '../../Images/Icon_eye.svg';
import DropdownSelect from '../../Shared/UI/DropdownSelect';
import DateRangePicker from '../../Shared/UI/DateRangePicker';
import { TablePagination } from 'Shared/Table';
import useNetworkLoader from 'hooks/useNetworkLoader';
import useDebounce from 'hooks/useDebounce';
import { formatAmount } from 'Utils/ulilities';
import { getReglements } from './ReglementServices';
import AttestationImpayeModal from './AttestationImpayeModal';

/* ── Status config ────────────────────────────────────────────────── */
const STATUS_CONFIG = {
  PAYE:   { label: 'Payé',   color: '#12B981', bg: '#ECFEF6',  border: '#12B981' },
  IMPAYE: { label: 'Impayé', color: '#EF456E', bg: '#EF456E05',  border: '#EF456E' },
};

const resolveStatutKey = (statut) => {
  if (!statut) return null;
  const s = statut.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  if (s.includes('impaye')) return 'IMPAYE';
  if (s.includes('paye')) return 'PAYE';
  return statut.toUpperCase();
};

const StatusBadge = ({ statut }) => {
  const key = resolveStatutKey(statut);
  const cfg = STATUS_CONFIG[key] || { label: statut || '—', color: '#666', bg: '#F5F5F5', border: '#CCC' };
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '5px', px: '10px', py: '4px', borderRadius: '20px', border: `1px solid ${cfg.border}`, backgroundColor: cfg.bg, whiteSpace: 'nowrap' }}>
      <Box sx={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: cfg.color }} />
      <Typography sx={{ fontSize: '11px', fontWeight: 600, color: cfg.color }}>{cfg.label}</Typography>
    </Box>
  );
};

/* ── Options ──────────────────────────────────────────────────────── */
const TYPE_OPTIONS = [
  { value: 'CHEQUE',   label: 'Chèque' },
  { value: 'TRAITE',   label: 'Traite' },
  { value: 'VIREMENT', label: 'Virement' },
  { value: 'LCN',      label: 'LCN' },
  { value: 'ESPECE',   label: 'Espèce' },
];

const TYPE_LABEL = TYPE_OPTIONS.reduce((acc, o) => ({ ...acc, [o.value]: o.label }), {});

const STATUT_OPTIONS = [
  { value: 'paye',   label: 'Payé' },
  { value: 'impaye', label: 'Impayé' },
];

/* ── Table cells ─────────────────────────────────────────────────── */
const TH = ({ children, width, sortKey, onSort, activeSortKey, sortDir }) => {
  const isActive = sortKey && activeSortKey === sortKey;
  return (
    <TableCell
      sx={{ fontWeight: 600, fontSize: '14px', color: '#385576', letterSpacing: '0.5px', textTransform: 'uppercase', borderBottom: '1px solid #D1D5DB', py: '8px', px: '12px', width, whiteSpace: 'nowrap', cursor: sortKey ? 'pointer' : 'default', userSelect: 'none', backgroundColor: '#FFFFFF', '&:first-of-type': { pl: '24px', borderTopLeftRadius: '9px' }, '&:last-of-type': { borderTopRightRadius: '9px' } }}
      onClick={sortKey && onSort ? () => onSort(sortKey) : undefined}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '1px' }}>
        {children}
        {sortKey && (
          isActive && sortDir === 'asc'
            ? <KeyboardArrowUpIcon sx={{ fontSize: 16, color: isActive ? '#385576' : '#C0C8D4' }} />
            : <KeyboardArrowDownIcon sx={{ fontSize: 16, color: isActive ? '#385576' : '#C0C8D4' }} />
        )}
      </Box>
    </TableCell>
  );
};

const TD = ({ children }) => (
  <TableCell sx={{ fontSize: '14px', color: '#000000', fontWeight: 600, borderBottom: '1px solid #D1D5DB', py: '3px', px: '12px', whiteSpace: 'nowrap', '&:first-of-type': { pl: '24px' } }}>
    {children}
  </TableCell>
);

/* ── Main component ───────────────────────────────────────────────── */
const Reglements = () => {
  const { callApi } = useNetworkLoader();
  const history = useHistory();
  const [openAttestation, setOpenAttestation] = useState(false);

  const [loading, setLoading] = useState(true);
  const [reglements, setReglements] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(10);

  const [search, setSearch] = useState('');
  const [type, setType] = useState([]);
  const [statut, setStatut] = useState([]);
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const [sortKey, setSortKey] = useState('rgDateEcheance');
  const [sortDir, setSortDir] = useState('desc');

  const debouncedSearch = useDebounce(search, 500);
  const effectiveSearch = debouncedSearch.length >= 2 ? debouncedSearch : '';

  const handleSort = (key) => {
    if (sortKey === key) setSortDir((d) => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
    setPage(1);
  };

  /* Load reglements from API */
  useEffect(() => {
    setLoading(true);
    const formatLocalDate = (d) => {
      const dt = new Date(d);
      return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
    };
    const startEcheance = dateRange.start ? formatLocalDate(dateRange.start) : '';
    const endEcheance = dateRange.end ? formatLocalDate(dateRange.end) : '';
    callApi(
      () => getReglements({
        page: page - 1,
        size,
        sort: `${sortKey},${sortDir}`,
        search: effectiveSearch,
        types: type,
        statuts: statut,
        startEcheance,
        endEcheance,
      }),
      (data) => {
        setReglements(data?.content || []);
        setTotal(data?.totalElements || 0);
        setLoading(false);
      },
      false,
    );
  }, [page, size, sortKey, sortDir, effectiveSearch, type, statut, dateRange]);

  const handleReset = () => {
    setSearch(''); setType([]); setStatut([]); setDateRange({ start: null, end: null }); setPage(1);
  };

  // const handleDownload = (r) => { ... }; — hidden, functionality not yet developed

  return (
    <Box sx={{ backgroundColor: '#F8FAFC', pt: '90px', px: { xs: '16px', md: '40px' }, pb: '40px', mt: '-80px' }}>

      {/* Header */}
      <Box sx={{ mb: '16px', mt: '8px' }}>
        <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#000000' }}>
          Mes règlements
          <Box component="span" sx={{ fontSize: '20px', fontWeight: 700, color: '#000000', ml: '4px' }}>
            {loading ? <>(<Skeleton component="span" variant="text" width={24} sx={{ display: 'inline-block', verticalAlign: 'middle' }} />)</> : `(${total})`}
          </Box>
        </Typography>
        <Typography sx={{ fontSize: '13px', color: '#385576', fontWeight: 600 }}>
          Gérez et consultez votre historique de transactions
        </Typography>
      </Box>

      {/* Filter bar */}
      <Box sx={{ position: 'sticky', top: '62px', zIndex: 10, backgroundColor: '#F8FAFC', pt: '8px', pb: '8px', mb: '10px' }}>
      <Box sx={{ backgroundColor: '#fff', border: '1px solid #DEE5EE', borderRadius: '10px', p: '6px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'nowrap' }}>
        <Box sx={{ flex: 2, minWidth: 0, height: '45px', display: 'flex', alignItems: 'center', border: '1px solid #DEE5EE', borderRadius: '8px', px: '12px', gap: '8px', backgroundColor: '#F8FAFC' }}>
          <Box sx={{ width: 20, height: 20, flexShrink: 0, backgroundColor: '#8397AE', WebkitMaskImage: `url(${searchIcon})`, maskImage: `url(${searchIcon})`, WebkitMaskRepeat: 'no-repeat', maskRepeat: 'no-repeat', WebkitMaskSize: 'contain', maskSize: 'contain', WebkitMaskPosition: 'center', maskPosition: 'center' }} />
          <input
            className="rg-search"
            placeholder="Rechercher par N° règlement ou montant..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            style={{ flex: 1, fontSize: '12px', color: '#1A2035', background: 'none', border: 'none', outline: 'none', width: '100%', fontFamily: "'Open Sans', sans-serif" }}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <DropdownSelect options={TYPE_OPTIONS} value={type} onChange={(val) => { setType(val); setPage(1); }} placeholder="Type" width="100%" multiple />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <DropdownSelect options={STATUT_OPTIONS} value={statut} onChange={(val) => { setStatut(val); setPage(1); }} placeholder="Statut" width="100%" multiple />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <DateRangePicker placeholder="Date échéance" value={dateRange} onApply={(start, end) => { setDateRange({ start, end }); setPage(1); }} width="100%" />
        </Box>
        <Button onClick={handleReset} variant="contained"
          sx={{ height: '45px', px: '20px', borderRadius: '8px', backgroundColor: '#007BC6', textTransform: 'none', fontWeight: 600, fontSize: '13px', flexShrink: 0, whiteSpace: 'nowrap', '&:hover': { backgroundColor: '#005F9E' } }}>
          Réinitialiser
        </Button>
      </Box>
      </Box>

      {/* Table */}
      <Box sx={{ backgroundColor: '#fff', border: '1px solid #D1D5DB', borderRadius: '10px', overflow: 'visible' }}>
        <TableContainer sx={{ overflow: 'visible' }}>
          <Table stickyHeader sx={{ '& .MuiTableCell-stickyHeader': { top: '135px', backgroundColor: '#FFFFFF' } }}>
            <TableHead sx={{ backgroundColor: '#FFFFFF' }}>
              <TableRow>
                <TH width={160} sortKey="reference" onSort={handleSort} activeSortKey={sortKey} sortDir={sortDir}>Référence</TH>
                <TH width={130} sortKey="type" onSort={handleSort} activeSortKey={sortKey} sortDir={sortDir}>Type</TH>
                <TH width={220} sortKey="amount" onSort={handleSort} activeSortKey={sortKey} sortDir={sortDir}>Montant</TH>
                <TH width={180} sortKey="rgDateEcheance" onSort={handleSort} activeSortKey={sortKey} sortDir={sortDir}>Date d'échéance</TH>
                <TH width={130}>Statut</TH>
                <TH width={160}>Date d'impayé</TH>
                <TH width={140}>Documents liés</TH>
                <TH width={100}>Action</TH>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: size }).map((_, i) => (
                  <TableRow key={i} sx={{ backgroundColor: i % 2 === 0 ? '#fff' : '#F8FAFC' }}>
                    <TableCell sx={{ borderBottom: '1px solid #D1D5DB', py: '8px', pl: '24px', pr: '12px' }}><Skeleton variant="text" width="80%" height={22} /></TableCell>
                    <TableCell sx={{ borderBottom: '1px solid #D1D5DB', py: '8px', px: '12px' }}><Skeleton variant="text" width="60%" height={22} /></TableCell>
                    <TableCell sx={{ borderBottom: '1px solid #D1D5DB', py: '8px', px: '12px' }}>
                      <Skeleton variant="text" width="85%" height={22} />
                      <Skeleton variant="text" width="65%" height={16} />
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid #D1D5DB', py: '8px', px: '12px' }}><Skeleton variant="text" width="70%" height={22} /></TableCell>
                    <TableCell sx={{ borderBottom: '1px solid #D1D5DB', py: '8px', px: '12px' }}><Skeleton variant="rectangular" width={75} height={26} sx={{ borderRadius: '20px' }} /></TableCell>
                    <TableCell sx={{ borderBottom: '1px solid #D1D5DB', py: '8px', px: '12px' }}><Skeleton variant="text" width="65%" height={22} /></TableCell>
                    <TableCell sx={{ borderBottom: '1px solid #D1D5DB', py: '8px', px: '12px' }}><Skeleton variant="text" width="30%" height={22} /></TableCell>
                    <TableCell sx={{ borderBottom: '1px solid #D1D5DB', py: '8px', px: '12px' }}>
                      <Box sx={{ display: 'flex', gap: '14px', alignItems: 'center' }}>
                        <Skeleton variant="circular" width={20} height={20} />
                        <Skeleton variant="circular" width={20} height={20} />
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              ) : reglements.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} sx={{ textAlign: 'center', py: '60px', color: '#8F8F8F', fontSize: '14px' }}>
                    Aucun règlement trouvé
                  </TableCell>
                </TableRow>
              ) : (
                reglements.map((r, idx) => {
                  const statutKey = resolveStatutKey(r.statut);
                  const isImpaye = statutKey === 'IMPAYE';
                  const bgColor = idx % 2 === 0 ? '#fff' : '#F8FAFC';
                  return (
                    <TableRow key={r.id}
                      sx={{ backgroundColor: bgColor, '&:hover': { backgroundColor: '#EBF5FB' }, transition: 'background-color 0.15s' }}>

                      {/* Référence */}
                      <TableCell sx={{ fontSize: '16px', color: '#000000', fontWeight: 600, borderBottom: '1px solid #D1D5DB', py: '3px', pl: '24px', pr: '12px', whiteSpace: 'nowrap', position: 'relative' }}>
                        {isImpaye && (
                          <Box sx={{ position: 'absolute', left: 0, top: '6px', bottom: '6px', width: '3px', backgroundColor: '#EF456E', borderRadius: '0 2px 2px 0' }} />
                        )}
                        <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#000000' }}>{r.reference || '—'}</Typography>
                      </TableCell>

                      {/* Type */}
                      <TD>{r.type ? (TYPE_LABEL[r.type] || r.type) : '—'}</TD>

                      {/* Montant */}
                      <TableCell sx={{ borderBottom: '1px solid #D1D5DB', py: '3px', px: '12px' }}>
                        <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#000000' }}>
                          {formatAmount(r.amount || 0)}
                        </Typography>
                        <Typography sx={{ fontSize: '11px', color: '#707070', fontWeight: 600, mt: '1px' }}>
                          Attaché: {formatAmount(r.montantAttache ?? 0)}
                          {' | '}
                          <Box component="span" sx={{ color: isImpaye ? '#EF456E' : (r.montantRestant || 0) === 0 ? '#00D69F' : '#FEA26F', fontWeight: 600, fontSize: '11px' }}>
                            Restant: {formatAmount(r.montantRestant ?? 0)}
                          </Box>
                        </Typography>
                      </TableCell>

                      {/* Date échéance */}
                      <TD>{r.dateEcheance ? new Date(r.dateEcheance).toLocaleDateString('fr-FR') : '—'}</TD>

                      {/* Statut */}
                      <TableCell sx={{ borderBottom: '1px solid #D1D5DB', py: '3px', px: '12px' }}>
                        <StatusBadge statut={r.statut} />
                      </TableCell>

                      {/* Date impayé */}
                      <TableCell sx={{ borderBottom: '1px solid #D1D5DB', py: '3px', px: '12px' }}>
                        {isImpaye && r.rgImpaye ? (
                          <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#EF456E' }}>
                            {new Date(r.rgImpaye).toLocaleDateString('fr-FR')}
                          </Typography>
                        ) : (
                          <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#000000' }}>—</Typography>
                        )}
                      </TableCell>

                      {/* Documents liés */}
                      <TableCell sx={{ borderBottom: '1px solid #D1D5DB', py: '3px', px: '12px' }}>
                        <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#000000' }}>{r.documentsCount ?? 0}</Typography>
                      </TableCell>

                      {/* Action */}
                      <TableCell sx={{ borderBottom: '1px solid #D1D5DB', py: '3px', px: '12px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                          <Tooltip title={r.documentsCount > 0 ? "Voir le détail" : ""} placement="top" arrow>
                            <Box
                              onClick={r.documentsCount > 0 ? () => history.push(`/reglements/${r.id}`) : undefined}
                              sx={{ display: 'inline-flex', cursor: r.documentsCount > 0 ? 'pointer' : 'default', opacity: r.documentsCount > 0 ? 1 : 0.3, '&:hover img': r.documentsCount > 0 ? { filter: 'brightness(0) saturate(100%) invert(29%) sepia(89%) saturate(876%) hue-rotate(182deg) brightness(100%) contrast(101%)', transition: 'filter 0.15s' } : {}, '& img': { transition: 'filter 0.15s' } }}
                            >
                              <img src={iconEye} alt="view" style={{ width: 20, height: 24, objectFit: 'contain' }} />
                            </Box>
                          </Tooltip>
                          {/* Download button hidden — functionality not yet developed */}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          page={page}
          pageSize={size}
          total={total}
          onChange={(newPage) => setPage(newPage)}
          pageSizeOptions={[10, 20, 50]}
          onPageSizeChange={(s) => { setSize(s); setPage(1); }}
        />
      </Box>

      <AttestationImpayeModal open={openAttestation} onClose={() => setOpenAttestation(false)} />
    </Box>
  );
};

export default Reglements;
